import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import { toast } from "sonner";

const ISSUE_LABELS = {
  missing_play_count: "play_count missing",
  missing_promo_plays: "promotional_plays missing",
  negative_values: "negative values",
  tier_without_promo: "tier applied without promo plays",
  cached_drift: "cached_display_plays drifted",
};

const StatTile = ({ label, value, color = "purple", testid }) => (
  <div className={`rounded-lg bg-${color}-500/10 border border-${color}-500/30 px-3 py-2`} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
    <div className="text-2xl font-black text-white">{(value || 0).toLocaleString()}</div>
  </div>
);

const AdminPlayCountAuditPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [includeClean, setIncludeClean] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/auth/me");
        if (!me.data || me.data.role !== "admin") {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        setUser(me.data);
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate]);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/play-counts/audit", {
        params: { limit: 200, include_clean: includeClean },
      });
      setAudit(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit");
    } finally {
      setLoading(false);
    }
  }, [includeClean]);

  useEffect(() => { if (user) fetchAudit(); }, [user, fetchAudit]);

  const handleRepair = async () => {
    if (!window.confirm("Normalise play_count / promotional_plays / cached_display_plays for every drifted row? Idempotent.")) return;
    setRepairing(true);
    try {
      const r = await api.post("/admin/play-counts/repair", {});
      toast.success(`Repaired ${r.data.repaired} row(s) of ${r.data.scanned} scanned.`);
      await fetchAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  if (!user) return <div className="min-h-screen bg-black flex items-center justify-center text-gray-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-black" data-testid="admin-play-count-audit-page">
      <SharedNavigation currentUser={user} />
      <div className="pt-20 px-4 pb-12 max-w-6xl mx-auto">
        <header className="mb-6">
          <h1 className="text-3xl font-bold text-white">📊 Play Count Trust Audit</h1>
          <p className="text-gray-400 text-sm mt-1">
            Ensures <code className="text-purple-300">display_plays</code> is identical across every surface
            (Following · Featured · Spotlight · Rankings · Search · Profile · Share Cards · Recaps · Beats).
          </p>
        </header>

        {audit && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="audit-summary">
            <StatTile label="Scanned" value={audit.total_submissions_scanned} color="purple" testid="stat-scanned" />
            <StatTile label="Dirty rows" value={audit.total_dirty_rows} color="rose" testid="stat-dirty" />
            {Object.entries(audit.issues_by_type || {}).map(([k, v]) => (
              <StatTile key={k} label={ISSUE_LABELS[k] || k} value={v} color={v > 0 ? "amber" : "emerald"} testid={`stat-issue-${k}`} />
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <label className="text-xs text-gray-300 flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeClean}
              onChange={(e) => setIncludeClean(e.target.checked)}
              data-testid="include-clean-toggle"
            />
            Include clean rows in table
          </label>
          <div className="flex gap-2">
            <Button
              onClick={fetchAudit}
              disabled={loading}
              variant="outline"
              className="border-purple-500/40 text-purple-200"
              data-testid="refresh-audit-btn"
            >
              {loading ? "Loading…" : "Refresh audit"}
            </Button>
            <Button
              onClick={handleRepair}
              disabled={repairing || (audit && audit.total_dirty_rows === 0)}
              className="bg-gradient-to-r from-amber-600 to-rose-600 text-white"
              data-testid="repair-play-counts-btn"
            >
              {repairing ? "Repairing…" : audit && audit.total_dirty_rows === 0 ? "Nothing to repair" : "Normalise all rows"}
            </Button>
          </div>
        </div>

        {audit?.rows && audit.rows.length > 0 && (
          <Card className="p-3 bg-gray-900/40 border-gray-800 overflow-x-auto" data-testid="audit-rows-table">
            <table className="w-full text-sm">
              <thead className="text-left text-[11px] uppercase tracking-wider text-gray-400">
                <tr>
                  <th className="px-2 py-1.5">Submission</th>
                  <th className="px-2 py-1.5">Title</th>
                  <th className="px-2 py-1.5 text-right">Play count</th>
                  <th className="px-2 py-1.5 text-right">Promo plays</th>
                  <th className="px-2 py-1.5 text-right">Display</th>
                  <th className="px-2 py-1.5">Problems</th>
                </tr>
              </thead>
              <tbody>
                {audit.rows.map((r) => (
                  <tr
                    key={r.submission_id}
                    className={`border-t border-white/5 ${r.is_clean ? "" : "bg-rose-500/5"}`}
                    data-testid={`audit-row-${r.submission_id}`}
                  >
                    <td className="px-2 py-1.5 font-mono text-[11px] text-gray-400">{r.submission_id}</td>
                    <td className="px-2 py-1.5 text-white">{r.track_title || "—"}</td>
                    <td className="px-2 py-1.5 text-right text-gray-200">{(r.play_count ?? "—").toLocaleString?.() ?? r.play_count}</td>
                    <td className="px-2 py-1.5 text-right text-gray-200">{(r.promotional_plays ?? "—").toLocaleString?.() ?? r.promotional_plays}</td>
                    <td className="px-2 py-1.5 text-right text-amber-300 font-bold">{r.display_plays.toLocaleString()}</td>
                    <td className="px-2 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {r.problems.map((p) => (
                          <span key={p} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 border border-rose-400/30 text-rose-200">
                            {ISSUE_LABELS[p] || p}
                          </span>
                        ))}
                        {r.problems.length === 0 && (
                          <span className="text-[10px] text-emerald-300">✓ clean</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {audit && audit.rows.length === 0 && (
          <Card className="p-6 text-center bg-emerald-500/5 border-emerald-500/30" data-testid="audit-all-clean">
            <div className="text-3xl mb-2">✅</div>
            <p className="text-emerald-200 font-bold">All play counts are clean</p>
            <p className="text-gray-400 text-sm mt-1">Display_plays will compute identically across every surface.</p>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPlayCountAuditPage;
